/**
 * Phase 6 operator-workflow primitives — small visual elements re-used
 * across Inbox, AlertRules, Comparison, Pins rail, Annotations, Briefing.
 *
 * Each primitive enforces honesty rails inline:
 *   - ChangeBadge: distinguishes real-world vs data-quality events (icon
 *     + word + tooltip carrying the "act on the event" vs "check the feed,
 *     not the outbreak" guidance)
 *   - PinChip: surfaces typed pin (icon + decay-class tooltip) + stale flag
 *   - LocalOnlyLock: the unmissable "🔒 LOCAL ONLY · not shared" badge
 *     for annotations
 *   - TierPip: matches the existing threat-tier display contract
 */

import type { ChangeType, ThreatTier, PinType, PathogenFamily } from './types';
import { CHANGE_META, PIN_TYPE_META, TIER_META } from './types';

/** Threat-tier display pip (compact). */
export function TierPip({ tier, small = false }: { tier: ThreatTier; small?: boolean }) {
  const t = TIER_META[tier];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'var(--bm-font-mono)', fontSize: small ? 9.5 : 10.5,
      fontWeight: 700, letterSpacing: '.3px',
      color: t.cssVar, background: t.bgVar,
      border: `1px solid ${t.cssVar}55`, borderRadius: 999,
      padding: small ? '1px 6px' : '2px 8px', whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 11, height: 11, borderRadius: 3, background: t.cssVar,
        color: 'var(--bm-bg)', display: 'grid', placeItems: 'center',
        fontSize: 8, fontWeight: 800,
      }}>{tier}</span>{t.word}
    </span>
  );
}

/** Inbox change-type badge. Tooltip carries the honesty guidance —
 *  "Real-world change — act on the event" vs "Data-quality change — check
 *  the feed, not the outbreak". Crucial: this is the operator's first
 *  signal of what kind of action to take. */
export function ChangeBadge({ kind, small = false }: { kind: ChangeType; small?: boolean }) {
  const c = CHANGE_META[kind];
  return (
    <span
      title={c.real
        ? 'Real-world change — act on the event'
        : 'Data-quality change — check the feed, not the outbreak'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontFamily: 'var(--bm-font-mono)', fontSize: small ? 10 : 11,
        fontWeight: 700, letterSpacing: '.3px',
        color: c.cssVar, background: c.bgVar,
        border: `1px solid ${c.cssVar}55`, borderRadius: 5,
        padding: small ? '1px 6px' : '2px 8px', whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden>{c.icon}</span>{c.word}
      {!c.real && <span style={{ opacity: 0.7, fontWeight: 400 }}>· data</span>}
    </span>
  );
}

/** Pathogen family glyph — shape encodes family (CVD-safe: shape ≠ colour). */
export function FamilyGlyph({
  family, color = 'var(--bm-text-muted)', size = 16,
}: { family: PathogenFamily; color?: string; size?: number }) {
  const inner = (() => {
    switch (family) {
      case 'haemorrhagic': return <polygon points="12,3 21,19 3,19" />;
      case 'vector': return <polygon points="12,2 22,12 12,22 2,12" />;
      case 'enteric': return <rect x="4" y="4" width="16" height="16" rx="2" />;
      case 'zoonotic': return <polygon points="12,2 20,7 20,17 12,22 4,17 4,7" />;
      default: return <circle cx="12" cy="12" r="9" />;
    }
  })();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block', flex: 'none' }}>
      <g fill={color} stroke={color} strokeWidth="1.2" fillOpacity="0.88">{inner}</g>
    </svg>
  );
}

/** Pin chip — type icon + label + stale flag (when applicable). The stale
 *  flag is unmissable when present per CD honesty rail. */
export function PinChip({
  type, label, stale, asOfDays, tierColor,
}: { type: PinType; label: string; stale: boolean; asOfDays: number; tierColor: string }) {
  const t = PIN_TYPE_META[type];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderLeft: `3px solid ${tierColor}`,
      borderRadius: 5,
    }}>
      <span title={`${t.word} pin · ${t.decay} decay`} aria-label={`${t.word} pin`}
        style={{ fontFamily: 'var(--bm-font-mono)', color: 'var(--bm-text-subtle)' }}>{t.icon}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--bm-text)' }}>{label}</span>
      {stale && (
        <span
          title={`pinned datum is stale — ${asOfDays}d old`}
          style={{ color: 'var(--bm-pin-stale)', fontSize: 13 }}
          aria-label="stale"
        >⏳</span>
      )}
    </div>
  );
}

/** The local-only lock badge for annotations — must be UNMISSABLE per CD
 *  spec ("operators may write sensitive content; UI must make it visually
 *  obvious this stays inside the tenant"). */
export function LocalOnlyLock({ small = false }: { small?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontFamily: 'var(--bm-font-mono)', fontSize: small ? 9.5 : 11, fontWeight: 700,
      letterSpacing: '.3px',
      color: 'var(--bm-anno-local)',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-anno-local)55',
      borderRadius: 3,
      padding: small ? '1px 5px' : '2px 7px',
    }} title="Annotations are local-only by default — never shared outside this tenant">
      🔒 LOCAL ONLY{!small && <span style={{ opacity: 0.7, fontWeight: 500 }}>· not shared</span>}
    </span>
  );
}

/** Small icon button — for the Inbox action verbs (ack / snooze / pin /
 *  dismiss). On/off state with optional tone colour. */
export function IconBtn({
  icon, label, on, tone, onClick,
}: { icon: string; label: string; on?: boolean; tone?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={on}
      style={{
        display: 'inline-grid', placeItems: 'center',
        width: 28, height: 28, borderRadius: 5, cursor: 'pointer',
        fontSize: 13, lineHeight: 1,
        color: on ? (tone ?? 'var(--bm-accent, #6ea3ff)') : 'var(--bm-text-subtle)',
        borderColor: on ? (tone ?? 'var(--bm-accent-ring, rgba(110,163,255,.4))') : 'var(--bm-border)',
        borderWidth: 1, borderStyle: 'solid',
        background: on ? 'var(--bm-bg)' : 'transparent',
      }}
    >{icon}</button>
  );
}

/** Coverage bars — mini, for Inbox-row coverage-gap indicator (R1 spec).
 *  Pulls from the Phase 5 coverage primitive contract but smaller. */
export function CovBars6({ level = 1 }: { level?: 0 | 1 | 2 | 3 }) {
  const heights = [6, 9, 12];
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 12 }}
      aria-label={`coverage level ${level}`}
    >
      {heights.map((h, i) => (
        <span key={i} style={{
          width: 3.5, height: h, borderRadius: 1,
          background: i < level ? 'var(--bm-cov)' : 'var(--bm-cov-dim)',
        }} />
      ))}
    </span>
  );
}

/** Relative-time formatter (just now / Nh ago / Nd ago). */
export function relTime(iso: string, nowMs = Date.now()): string {
  const ms = nowMs - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return iso.slice(0, 10);
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
